import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import type { NavbarProviderOption } from "./ehr/navbarProvider.slice";

interface Permission {
  view: boolean;
  viewAll: boolean;
  update: boolean;
  create: boolean;
  delete: boolean;
}

interface Permissions {
  [key: string]: Permission;
}

interface AuthState {
  isAuthenticated: boolean;
  user: any | null;
  permissions: Permissions | null;
  assignedLocations: Array<any> | [];
  currentLocation: string | null;
  snackbar: {
    isOpen: boolean;
    message: string | null;
    severity: "success" | "error" | "warning" | "info";
  };
  isDeviceRegistered: boolean;
  shouldRefetchProfile: boolean;
  isLoadingUserData: boolean;
  // Persisted role selection for Roles and Permissions
  persistedRoleType: string | null;
  persistedRole: string | null;
  persistedRoleTypeName: string | null;
  persistedRoleName: string | null;
  // Providers picked in the navbar dropdown; kept here so they survive reloads.
  persistedNavbarProviders: NavbarProviderOption[];
  /** Location whose care-team providers were last pre-picked for a nurse. */
  persistedNurseDefaultsLocation: string | null;
  /** That location's care-team providers; the nurse cannot deselect them. */
  persistedNurseCareTeam: NavbarProviderOption[];
}

const initialState: AuthState = {
  isAuthenticated: false,
  user: null,
  permissions: null,
  assignedLocations: [],
  currentLocation: null,
  snackbar: {
    isOpen: false,
    message: null,
    severity: "success",
  },
  isDeviceRegistered: false,
  shouldRefetchProfile: false,
  isLoadingUserData: false,
  persistedRoleType: null,
  persistedRole: null,
  persistedRoleTypeName: null,
  persistedRoleName: null,
  persistedNavbarProviders: [],
  persistedNurseDefaultsLocation: null,
  persistedNurseCareTeam: [],
};

const authSlice = createSlice({
  name: "auth",
  initialState,
  reducers: {
    triggerRefetchProfile: (state) => {
      state.shouldRefetchProfile = true;
    },
    setLoadingUserData: (state, action: PayloadAction<boolean>) => {
      state.isLoadingUserData = action.payload;
    },
    setProfile: (state, action: PayloadAction<any>) => {
      state.user = action.payload;
      state.shouldRefetchProfile = false;
      state.isLoadingUserData = false;
    },
    setPermissions: (state, action: PayloadAction<Permissions>) => {
      state.permissions = action.payload;
    },
    setAssignedLocations: (state, action: PayloadAction<any>) => {
      state.assignedLocations = action.payload;
    },
    setCurrentLocation: (state, action: PayloadAction<any>) => {
      state.currentLocation = action.payload;
    },
    login: (state, action: PayloadAction<string>) => {
      state.isAuthenticated = true;
      state.user = action.payload;
    },
    setIsDeviceRegistered: (state, action: PayloadAction<boolean>) => {
      state.isDeviceRegistered = action.payload;
    },
    logout: (state) => {
      return initialState;
    },
    showSnackbar: (
      state,
      action: PayloadAction<{
        message: string;
        severity: "success" | "error" | "warning" | "info";
      }>,
    ) => {
      state.snackbar = {
        isOpen: true,
        message: action.payload.message,
        severity: action.payload.severity,
      };
    },
    hideSnackbar: (state) => {
      state.snackbar = {
        isOpen: false,
        message: null,
        severity: "success",
      };
    },
    // Actions for persisting role selection
    setPersistedRoleSelection: (
      state,
      action: PayloadAction<{
        roleType: string;
        role: string;
        roleTypeName: string;
        roleName: string;
      }>,
    ) => {
      state.persistedRoleType = action.payload.roleType;
      state.persistedRole = action.payload.role;
      state.persistedRoleTypeName = action.payload.roleTypeName;
      state.persistedRoleName = action.payload.roleName;
    },
    clearPersistedRoleSelection: (state) => {
      state.persistedRoleType = null;
      state.persistedRole = null;
      state.persistedRoleTypeName = null;
      state.persistedRoleName = null;
    },
    setPersistedNavbarProviders: (
      state,
      action: PayloadAction<NavbarProviderOption[]>,
    ) => {
      state.persistedNavbarProviders = action.payload;
    },
    setPersistedNurseDefaultsLocation: (
      state,
      action: PayloadAction<string | null>,
    ) => {
      state.persistedNurseDefaultsLocation = action.payload;
    },
    setPersistedNurseCareTeam: (
      state,
      action: PayloadAction<NavbarProviderOption[]>,
    ) => {
      state.persistedNurseCareTeam = action.payload;
    },
  },
});

export const {
  login,
  logout,
  showSnackbar,
  hideSnackbar,
  setIsDeviceRegistered,
  setProfile,
  setPermissions,
  setAssignedLocations,
  setCurrentLocation,
  triggerRefetchProfile,
  setLoadingUserData,
  setPersistedRoleSelection,
  clearPersistedRoleSelection,
  setPersistedNavbarProviders,
  setPersistedNurseDefaultsLocation,
  setPersistedNurseCareTeam,
} = authSlice.actions;

export default authSlice.reducer;
